"use client"
import Link from "next/link"
import { useSearchParams } from "next/navigation"
import React, { HTMLAttributes, memo, Suspense } from "react"
import { UrlObject } from "url"
import { keepOnlyUtmParams, mergeTrackingParams } from "./utils/utmParams"

type NextLinkProps = HTMLAttributes<HTMLAnchorElement> & {
  href: string
  openInNewTab?: boolean
  children: React.ReactNode
  noScrollToTop?: boolean
  noEncoding?: boolean
}

const NextLinkWithSearchParams: React.FunctionComponent<NextLinkProps> = ({
  href,
  children,
  openInNewTab = false,
  noScrollToTop = false,
  noEncoding = false,
  ...props
}) => {
  const searchParams = useSearchParams()

  // Extract the UTM parameters from the current URL search string
  const utmSearch = keepOnlyUtmParams(searchParams?.toString() || "")

  // Extract the hash from the href
  const [hrefWithoutHash, onlyHash] = href ? href.split("#") : [""]

  // Extract the pathname and search string from the link
  const [hrefWithoutSearch, onlySearch] = hrefWithoutHash.split("?")

  // Combine the search string from the link and the UTM parameters
  const combinedSearchParams = mergeTrackingParams(onlySearch || "", utmSearch)

  // Create the URL object for the link
  const link: UrlObject = {
    pathname: hrefWithoutSearch,
    search: combinedSearchParams,
    hash: onlyHash ?? null,
  }

  return (
    <Link
      href={noEncoding ? href : link}
      scroll={!noScrollToTop}
      prefetch={false}
      target={openInNewTab ? "_blank" : "_self"}
      rel={openInNewTab ? "noreferrer" : ""}
      {...props}
    >
      {children}
    </Link>
  )
}

const NextLink: React.FunctionComponent<NextLinkProps> = ({
  href,
  children,
  openInNewTab = false,
  noScrollToTop = false,
  noEncoding = false,
  ...props
}) => {
  return (
    <Suspense
      fallback={
        <Link
          href={href}
          scroll={!noScrollToTop}
          prefetch={false}
          target={openInNewTab ? "_blank" : "_self"}
          rel={openInNewTab ? "noreferrer" : ""}
          {...props}
        >
          {children}
        </Link>
      }
    >
      <NextLinkWithSearchParams
        href={href}
        openInNewTab={openInNewTab}
        noScrollToTop={noScrollToTop}
        noEncoding={noEncoding}
        {...props}
      >
        {children}
      </NextLinkWithSearchParams>
    </Suspense>
  )
}

export default memo(NextLink)
